import React, { useEffect, useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { FetchStatus, FetchType } from '../../../../enums';
import { Entity } from '../../../../models/view/entity';
import { useAppActions, useAppState } from '../../../../overmind';
import { AdditionalLearningViewModel, additionalLearningTypeIcons } from '../additional-learning.models';
import { IAdditionalLearningFocusPanelLayoutProps } from './additional-learning.models';
import { FocusPanelLoaderLayer } from '../../../../components/general/loading-state/loader-layers/focus-panel-loader-layer/focus-panel-loader-layer';
import { AddIcon, Anchor, Button, PanelHeader, FormGroup, GridItemLayout, GridLayout, ListItemLayout, ListLayout, PillButton, RadioButton, RadioButtonCard, RequiredValidator, URLValidator, XIcon, TextField, FieldController, NameValidator } from '@keplerco/core';
import { FuzzySearchParams } from '../../../../components/inputs/fuzzy-search/fuzzy-search.models';
import { Datepicker } from '../../../../components/inputs/datepicker/datepicker';
import { FuzzySearch } from '../../../../components/inputs/fuzzy-search/fuzzy-search';
import { StarRating } from '../../../../components/inputs/star-rating/star-rating';
import classNames from 'classnames';

export function AdditionalLearningPanel(props: IAdditionalLearningFocusPanelLayoutProps): JSX.Element {
  const actions = useAppActions();
  const { user, fetchState } = useAppState();

  const skillsRef = useRef<HTMLDivElement>(null);

  const [formValues, setFormValues] = useState<FieldValues>();
  const [skills, setSkills] = useState<Entity[]>();
  const [selectedSkills, setSelectedSkills] = useState<Entity[]>([]);
  const [skillsDirty, setSkillsDirty] = useState<boolean>(false);

  const { control, handleSubmit, setValue, watch } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });

  useEffect(() => {
    watch(setFormValues);
  }, []);

  useEffect(() => {
    async function getData() {
      if (!props.slug) return;

      actions.startLoader({ path: props.path, type: FetchType.DialogFetching });

      const data = await actions.getAdditionalLearning(props.slug);

      actions.stopLoader(props.path);

      if (!data) return;

      setValue('type', data.type.toString());
      setValue('name', data.name);
      setValue('webAddress', data.webAddress);
      setValue('rating', data.rating);
      setValue('dateStarted', data.dateStarted);
      setValue('dateCompleted', data.dateCompleted);

      setSelectedSkills(data.skills);
    }

    getData();
  }, [props.slug]);

  useEffect(() => {
    async function getData() {
      if (!props.skillSlug || (!!selectedSkills && selectedSkills.length > 0)) return;

      actions.startLoader({ path: props.path, type: FetchType.DialogFetching });

      const data = await actions.searchLearnerSkills('');
      setSelectedSkills(data?.filter(skill => skill.slug === props.skillSlug) ?? []);

      actions.stopLoader(props.path);
    }

    getData();
  }, [props.skillSlug]);

  async function onFuzzySearchHandler(searchParams: FuzzySearchParams) {
    if (!searchParams.query) return setSkills([]);

    actions.startLoader({ path: props.path, type: FetchType.PageFetching });
    const data = await actions.searchLearnerSkills(searchParams.query);
    setSkills(data);
    actions.stopLoader(props.path);
  }

  function onAddPillActionedHandler(skill: Entity) {
    setSelectedSkills([...selectedSkills, skill]);
    setSkillsDirty(false);
  }

  function onRemovePillActionedHandler(skillId: number) {
    const nextState = [...selectedSkills.filter(temp => temp.id !== skillId)];
    setSelectedSkills(nextState);
    setSkillsDirty(nextState.length === 0);
  }

  return (
    <FocusPanelLoaderLayer path={props.path}>
      <div className="panelContent">
        <PanelHeader title="Track additional learning" />

        <form
          id="additionalLearning"
          className="panelForm"
          onSubmit={handleSubmit(async fieldValues => {
            if (!selectedSkills || selectedSkills.length === 0) return setTimeout(() => skillsRef.current?.scrollIntoView());

            const doReflection = fieldValues.doReflection === 'yes';
            delete fieldValues.doReflection;

            const model = {
              ...fieldValues,
              slug: props.slug,
              type: Number(fieldValues.type),
              skills: selectedSkills,
            } as AdditionalLearningViewModel;

            props.onSubmit(model, doReflection);
          })}
        >
          <div>
            <FieldController
              control={control}
              name="type"
              rules={new RequiredValidator('Please select a type')}
              render={({ field, fieldState }) => (
                <FormGroup
                  label="What would you like to track?"
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Please select a type',
                  }}
                >
                  <GridLayout columnMinWidth={100}>
                    {additionalLearningTypeIcons.map(typeIcon => (
                      <GridItemLayout key={typeIcon.type}>
                        <RadioButtonCard {...field} id={typeIcon.name} value={typeIcon.type} checked={field.value === typeIcon.type.toString()} checkicon>
                          <div className="card" style={{ padding: 10 }}>
                            <div className="toggleCardVerticalContentLayout" style={{ gap: 0 }}>
                              <div style={{ overflow: 'hidden', margin: '0 auto' }}>
                                <img src={typeIcon.iconUrl} aria-hidden />
                              </div>

                              <label htmlFor={typeIcon.name} className="caption" style={{ textAlign: 'center', minHeight: 40, width: '100%' }}>
                                {typeIcon.description}
                              </label>
                            </div>
                          </div>
                        </RadioButtonCard>
                      </GridItemLayout>
                    ))}
                  </GridLayout>
                </FormGroup>
              )}
            />

            {fetchState[props.path].status === FetchStatus.Active && fetchState[props.path].type === FetchType.DialogFetching ? (
              <React.Fragment />
            ) : (
              <FormGroup label="When did you complete it?" paddingTop={24}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <FieldController
                    control={control}
                    name="dateStarted"
                    rules={new RequiredValidator('Please select a start date')}
                    render={({ field, fieldState }) => {
                      const defaultDate = !!field.value ? new Date(field.value) : new Date();
                      const maxDate = formValues?.dateCompleted instanceof Date ? formValues.dateCompleted : new Date();

                      return (
                        <div>
                          <Datepicker autoHeight defaultDate={defaultDate} haserror={!!fieldState.error && !field.value} maxDate={maxDate} label="Date started" onDateSelected={value => field.onChange(value)} />
                          <div className={classNames('fieldErrorMessage', { invisible: !fieldState.error || !!field.value })}>{fieldState.error?.message}</div>
                        </div>
                      ); // TODO: why is fieldState not updating?
                    }}
                  />

                  <FieldController
                    control={control}
                    name="dateCompleted"
                    rules={new RequiredValidator('Please select a completion date')}
                    render={({ field, fieldState }) => {
                      const defaultDate = !!field.value ? new Date(field.value) : new Date();
                      const maxDate = new Date();

                      let minDate: Date | undefined = void 0;
                      if (!!formValues?.dateStarted) {
                        minDate = new Date(formValues?.dateStarted);
                        minDate.setDate(minDate.getDate() - 1);
                      }

                      return (
                        <div>
                          <Datepicker autoHeight defaultDate={defaultDate} haserror={!!fieldState.error && !field.value} minDate={minDate} maxDate={maxDate} label="Date completed" onDateSelected={value => field.onChange(value)} />
                          <div className={classNames('fieldErrorMessage', { invisible: !fieldState.error || !!field.value })}>{fieldState.error?.message}</div>
                        </div>
                      ); // TODO: why is fieldState not updating?
                    }}
                  />
                </div>
              </FormGroup>
            )}

            <FormGroup label="What was it about?" paddingTop={24}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <FieldController
                  control={control}
                  name="name"
                  rules={new NameValidator()}
                  render={({ field, fieldState }) => {
                    return (
                      <TextField
                        {...field}
                        type="text"
                        label="Name"
                        validation={{
                          dirty: fieldState.isDirty || !!fieldState.error,
                          invalid: !!fieldState.error,
                          message: fieldState.error?.message ?? 'Please enter a name',
                        }}
                      />
                    );
                  }}
                />

                <FieldController
                  control={control}
                  name="webAddress"
                  rules={new URLValidator(false)}
                  render={({ field, fieldState }) => {
                    return (
                      <TextField
                        {...field}
                        type="url"
                        label="Web address"
                        validation={{
                          dirty: fieldState.isDirty || !!fieldState.error,
                          invalid: !!fieldState.error,
                          message: fieldState.error?.message ?? 'Enter your password',
                        }}
                      />
                    );
                  }}
                />
              </div>
            </FormGroup>

            <FormGroup label="What skills were you developing?" validation={{ dirty: skillsDirty, invalid: selectedSkills?.length === 0, message: 'Please select at least one skill' }}>
              <div ref={skillsRef}>
                <FuzzySearch boxedResults disableEmptyState label="Start typing..." loading={fetchState[props.path].type === FetchType.PageFetching && fetchState[props.path].status === FetchStatus.Active} onFuzzySearch={onFuzzySearchHandler} fullWidthSearch={true}>
                  {!!skills &&
                    skills.length > 0 &&
                    skills
                      .filter(skill => !selectedSkills?.some(selectedSkill => selectedSkill.id === skill.id))
                      .map(skill => {
                        return <PillButton key={skill.id} id={skill.id} label={skill.name} icon={<AddIcon />} active backgroundColour="light" activeTextColour="black" onClick={() => onAddPillActionedHandler(skill)} />;
                      })}
                </FuzzySearch>

                {/* added skills */}
                {!!selectedSkills && selectedSkills.length > 0 && (
                  <div style={{ paddingTop: 15, paddingBottom: 15, display: 'flex', flexWrap: 'wrap', gap: 15 }}>
                    {selectedSkills.map(selectedSkill => {
                      return <PillButton key={selectedSkill.id} id={selectedSkill.id} label={selectedSkill.name} icon={<XIcon />} backgroundColour="baby-blue" onClick={() => onRemovePillActionedHandler(selectedSkill.id!)} />;
                    })}
                  </div>
                )}
              </div>
            </FormGroup>

            <FieldController
              control={control}
              rules={new RequiredValidator('Please enter a rating')}
              name="rating"
              render={({ field, fieldState }) => {
                return (
                  <FormGroup
                    label="Your rating:"
                    paddingTop={24}
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      // invalid: !!fieldState.error, // TODO: why is fieldState.error not updating?
                      invalid: !field.value, // TODO: why is fieldState.error not updating?
                      message: fieldState.error?.message ?? 'Please enter a rating',
                    }}
                  >
                    <StarRating {...field} id="rating" />
                  </FormGroup>
                );
              }}
            />

            {user?.allowReflections && !props.slug && (
              <FieldController
                control={control}
                rules={new RequiredValidator('Do you want to reflect on your learning?')}
                name="doReflection"
                render={({ field, fieldState }) => {
                  return (
                    <FormGroup label="Would you like to reflect on your learning now?" validation={{ dirty: fieldState.isDirty || !!fieldState.error, invalid: !!fieldState.error, message: fieldState.error?.message ?? 'Do you want to reflect on your learning?' }} paddingTop={24}>
                      <ListLayout wrap gap={20}>
                        <ListItemLayout>
                          <RadioButton id="yes" {...field} value="yes" checked={field.value === 'yes'}>
                            Yes
                          </RadioButton>
                        </ListItemLayout>

                        <ListItemLayout>
                          <RadioButton id="no" {...field} value="no" checked={field.value === 'no'}>
                            No, I will do this later
                          </RadioButton>
                        </ListItemLayout>
                      </ListLayout>
                    </FormGroup>
                  );
                }}
              />
            )}
          </div>

          <footer className="panelFooter" style={{ marginTop: 24 }}>
            {!!props.onCancel && <Anchor onClick={props.onCancel}>Cancel</Anchor>}

            <Button type="button" onClick={() => setSkillsDirty(true)}>
              Submit
            </Button>
          </footer>
        </form>
      </div>
    </FocusPanelLoaderLayer>
  );
}
